import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiEndpoint from '../../components/config/data';
import { getAuthHeader } from '../../utils/tokenService';

// Selector for user list
export const getUserList = state => {
  return state.user.users;
};

// Helper functions for localStorage
const saveUserToLocalStorage = user => {
  try {
    localStorage.setItem('userInfo', JSON.stringify(user));
  } catch (error) {
    console.error('Error saving user to localStorage:', error);
  }
};

const loadUserFromLocalStorage = () => {
  try {
    const userInfo = localStorage.getItem('userInfo');
    return userInfo ? JSON.parse(userInfo) : null;
  } catch (error) {
    console.error('Error loading user from localStorage:', error);
    return null;
  }
};

// Initialize state from localStorage if available
const userFromStorage = loadUserFromLocalStorage();

const initialState = userFromStorage
  ? {
      ...userFromStorage,
      loading: false,
      error: null,
      users: [],
    }
  : {
      userId: null,
      firstName: '',
      lastName: '',
      email: '',
      isAuthenticated: false,
      loading: false,
      error: null,
      users: [],
    };

// Async thunk to fetch user information from token (SSO or JWT)
// This endpoint works with both HttpOnly cookies (SSO) and JWT tokens
export const fetchUserInfoFromToken = createAsyncThunk(
  'user/fetchUserInfoFromToken',
  async (_, { rejectWithValue }) => {
    try {
      const authHeader = getAuthHeader();
      const response = await fetch(
        `${apiEndpoint}/api/v1/get_user_from_token/`,
        {
          method: 'GET',
          credentials: 'include',
          headers: {
            ...(authHeader ? { Authorization: authHeader } : {}),
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch user information from token');
      }

      const data = await response.json();

      // The endpoint returns: { id, email, first_name, last_name }
      const userInfo = {
        userId: data.id,
        firstName: data.first_name || '',
        lastName: data.last_name || '',
        email: data.email || '',
      };

      return userInfo;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk to fetch user information
export const fetchUserInfo = createAsyncThunk(
  'user/fetchUserInfo',
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetch(`${apiEndpoint}/api/get_users/`, {
        method: 'GET',
        credentials: 'include',
        headers: localStorage.getItem('access')
          ? { Authorization: 'Bearer ' + localStorage.getItem('access') }
          : {},
      });

      if (!response.ok) {
        throw new Error('Failed to fetch user information');
      }

      const data = await response.json();

      // Find the current user's data in the returned list
      // The API returns all users in the company, so we need to find the current user
      // by matching the user_id_id with the ID from the JWT token
      const currentUserId = localStorage.getItem('currentUserId');

      if (!data.data || !Array.isArray(data.data)) {
        throw new Error('Invalid data format received from API');
      }

      // Convert currentUserId to number for comparison
      const currentUserIdNum = parseInt(currentUserId);

      const currentUser = data.data.find(
        user => user.user_id_id === currentUserIdNum
      );

      if (!currentUser) {
        throw new Error('Current user not found in response');
      }

      const userInfo = {
        userId: currentUser.user_id_id,
        firstName: currentUser.first_name || '',
        lastName: currentUser.last_name || '',
        email: currentUser.email_id || '',
      };

      return { userInfo, users: data.data };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setUserInfo: (state, action) => {
      const { userId, firstName, lastName, email } = action.payload;
      state.userId = userId;
      state.firstName = firstName;
      state.lastName = lastName;
      state.email = email;
      state.isAuthenticated = true;

      // Save to localStorage
      saveUserToLocalStorage({
        userId,
        firstName,
        lastName,
        email,
        isAuthenticated: true,
      });
    },
    clearUserInfo: state => {
      state.userId = null;
      state.firstName = '';
      state.lastName = '';
      state.email = '';
      state.isAuthenticated = false;
      state.loading = false;
      state.error = null;

      // Clear from localStorage
      localStorage.removeItem('userInfo');
      localStorage.removeItem('currentUserId');
    },
    setUserId: (state, action) => {
      state.userId = action.payload;
      // Store the user ID in localStorage for use in the fetchUserInfo thunk
      localStorage.setItem('currentUserId', action.payload);
    },
    setUsers: (state, action) => {
      state.users = action.payload;
    },
  },
  extraReducers: builder => {
    builder
      // Handle fetchUserInfoFromToken (for SSO and JWT auth)
      .addCase(fetchUserInfoFromToken.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserInfoFromToken.fulfilled, (state, action) => {
        const userInfo = action.payload;
        state.loading = false;
        state.userId = userInfo.userId;
        state.firstName = userInfo.firstName;
        state.lastName = userInfo.lastName;
        state.email = userInfo.email;
        state.isAuthenticated = true;

        // Save to localStorage
        saveUserToLocalStorage({
          userId: userInfo.userId,
          firstName: userInfo.firstName,
          lastName: userInfo.lastName,
          email: userInfo.email,
          isAuthenticated: true,
        });

        // Also store currentUserId for compatibility
        localStorage.setItem('currentUserId', userInfo.userId);

        // Note: Heap Analytics user identification is handled by the HeapUserIdentifier component
        // to avoid duplicate tracking and ensure consistent login event tracking
      })
      .addCase(fetchUserInfoFromToken.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Handle fetchUserInfo (legacy method)
      .addCase(fetchUserInfo.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserInfo.fulfilled, (state, action) => {
        const { userInfo, users } = action.payload;
        state.loading = false;
        state.userId = userInfo.userId;
        state.firstName = userInfo.firstName;
        state.lastName = userInfo.lastName;
        state.email = userInfo.email;
        state.isAuthenticated = true;
        state.users = users;

        // Save to localStorage
        saveUserToLocalStorage({
          userId: action.payload.userId,
          firstName: action.payload.firstName,
          lastName: action.payload.lastName,
          email: action.payload.email,
          isAuthenticated: true,
        });
      })
      .addCase(fetchUserInfo.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

// Action creators are generated for each case reducer function
export const { setUserInfo, clearUserInfo, setUserId, setUsers } =
  userSlice.actions;

export default userSlice.reducer;
